/**
 * PanelWarningCallout.tsx
 *
 * Inline callout components for panel-level aggregated warnings (YoY Slice 2.3).
 *
 * Two callout variants:
 *
 *   NoPriorDataCallout   — informs the user that no prior-year data is available
 *                          for comparison. Rendered when `aggregatedWarnings`
 *                          contains a `PRIOR_SEASON_NO_DATA` entry.
 *                          - Informational only (NOT error, NOT warning amber).
 *                          - Treatment per mockup §38.5: muted background, muted
 *                            foreground text, 12px Info icon prefix, role="note".
 *                          - Copy: "No [YEAR] data available for comparison."
 *
 *   PartialFailureCallout — informs the user that some data couldn't be loaded
 *                          and offers a retry affordance. Rendered when
 *                          `aggregatedWarnings` contains a
 *                          `MULTI_FETCH_PARTIAL_FAILURE` entry.
 *                          - Informational (not an error card — panel still shows
 *                            available data). Same visual treatment as
 *                            NoPriorDataCallout but with an inline Retry button.
 *                          - role="note" per §38.5 / §38.6.
 *                          - `onRetry` is optional; when absent, the Retry button
 *                            is not rendered (future wiring via PanelCanvasV1).
 *
 * Both callouts:
 *   - Sit BETWEEN the panel's chart content and the stale-note separator.
 *   - Never fire when 1 season is selected — gated on the explicit warning code.
 *   - WCAG AA: #64748B on #F8FAFC = 4.76:1 contrast (brand-guide §38.5 note).
 *
 * Brand-guide reference: §38.5 (no-prior-data callout), §34.1 (no silent failures),
 * §34.2 (PRIOR_SEASON_NO_DATA → BUSINESS_EMPTY informational tier).
 *
 * YoY TDD §3.4.
 */

import React from 'react'
import { Info } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { PanelAggregatedWarning } from '@/types/DashboardV1.types'

// ─── Shared callout shell ─────────────────────────────────────────────────────

/**
 * Visual treatment per mockup §38.5:
 *   background: --muted (#F8FAFC in light mode)
 *   border:     --border
 *   text:       --muted-foreground (#64748B)
 *
 * margin-top: 12px places the callout directly below the chart/table content
 * and above the separator (stale note or card bottom), matching the mockup layout.
 */
const CALLOUT_CLASS = cn(
  'flex items-start gap-2',
  'mt-3 px-3 py-2.5',
  'bg-muted border border-border rounded-lg',
)

// ─── NoPriorDataCallout ───────────────────────────────────────────────────────

interface NoPriorDataCalloutProps {
  /**
   * The PRIOR_SEASON_NO_DATA warning. The `context.missingSeason` value is
   * interpolated into the copy when present (e.g. "No 2024 data available…").
   * When absent, falls back to the generic copy.
   */
  warning: PanelAggregatedWarning
}

/**
 * Informational callout for the no-prior-data degraded-comparison state.
 *
 * Per mockup:
 *   - role="note" (NOT role="alert") — this is a system note, not an error.
 *   - 12px Info icon (lucide-react), aria-hidden, muted-foreground color.
 *   - 11.5px muted-foreground text, font-weight: 400, line-height 1.4.
 *   - Copy: "No [YEAR] data available for comparison."
 *
 * The panel continues to render the current-season data above this callout.
 * No action affordance — purely informational.
 */
export const NoPriorDataCallout: React.FC<NoPriorDataCalloutProps> = ({ warning }) => {
  const missingSeason = warning.context?.missingSeason
  const copy = missingSeason
    ? `No ${missingSeason} data available for comparison.`
    : 'No prior-year data available for comparison.'

  const ariaLabel = missingSeason
    ? `No prior-year data available for ${missingSeason}`
    : 'No prior-year data available for comparison'

  return (
    <div
      className={CALLOUT_CLASS}
      role="note"
      aria-label={ariaLabel}
      data-testid="no-prior-data-callout"
    >
      <Info
        size={12}
        aria-hidden="true"
        className="text-muted-foreground flex-shrink-0 mt-[1px]"
      />
      <p
        className="text-muted-foreground font-normal leading-snug"
        style={{ fontSize: '11.5px' }}
      >
        {copy}
      </p>
    </div>
  )
}

// ─── PartialFailureCallout ────────────────────────────────────────────────────

interface PartialFailureCalloutProps {
  /**
   * The MULTI_FETCH_PARTIAL_FAILURE warning. Used for the aria-label only —
   * the copy is deterministic server-independent.
   */
  warning: PanelAggregatedWarning
  /**
   * Called when the user clicks "Retry". Fires the dashboard re-sync flow
   * from the parent (Canvas.tsx → PanelCanvasV1 → PanelRenderer).
   *
   * When absent, the Retry button is not rendered. This is the expected state
   * during the Slice 2.3 FE phase, before the parent wiring is completed in
   * Canvas.tsx. The callout still surfaces so the user is informed of the
   * partial failure.
   */
  onRetry?: () => void
}

/**
 * Informational callout for the multi-fetch partial-failure state.
 *
 * Same visual treatment as NoPriorDataCallout but includes an optional inline
 * Retry button when `onRetry` is provided.
 *
 * Per TDD §3.4 / §11.4 (CEO ratified 2026-05-27 — soft per-branch failure):
 * the panel renders with available data + this callout.
 *
 * role="note" — NOT role="alert". The user's dashboard still partially works;
 * this is an informational nudge, not a blocking error.
 */
export const PartialFailureCallout: React.FC<PartialFailureCalloutProps> = ({
  onRetry,
}: PartialFailureCalloutProps) => (
  <div
    className={CALLOUT_CLASS}
    role="note"
    aria-label="Some data could not be loaded"
    data-testid="partial-failure-callout"
  >
    <Info
      size={12}
      aria-hidden="true"
      className="text-muted-foreground flex-shrink-0 mt-[1px]"
    />
    <p
      className="text-muted-foreground font-normal leading-snug flex items-center gap-2"
      style={{ fontSize: '11.5px' }}
    >
      Some data couldn&apos;t be loaded.
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className={cn(
            'text-accent font-medium underline-offset-2 hover:underline',
            'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
            'bg-transparent border-0 cursor-pointer p-0',
          )}
          style={{ fontSize: '11.5px' }}
          aria-label="Retry loading panel data"
        >
          Retry
        </button>
      )}
    </p>
  </div>
)

// ─── PanelWarningCalloutGroup ─────────────────────────────────────────────────

interface PanelWarningCalloutGroupProps {
  /**
   * Warnings from `panelData.aggregatedWarnings`. The component renders
   * callouts for recognized codes (PRIOR_SEASON_NO_DATA,
   * MULTI_FETCH_PARTIAL_FAILURE) and silently ignores unknown codes — forward
   * compat for future warning types added on the BE.
   */
  warnings: PanelAggregatedWarning[]
  /**
   * Callback for the MULTI_FETCH_PARTIAL_FAILURE retry action. See
   * `PartialFailureCallout.onRetry` for the wire-up contract.
   */
  onRetry?: () => void
}

/**
 * Renders all recognized warning callouts for a panel.
 *
 * Typically at most one callout will be present (either PRIOR_SEASON_NO_DATA
 * or MULTI_FETCH_PARTIAL_FAILURE, not both). In the unlikely case both appear,
 * both are rendered stacked. Unknown warning codes are silently skipped.
 *
 * This component is the single integration point — `PanelRenderer` calls it
 * once with the full `aggregatedWarnings` array.
 */
export const PanelWarningCalloutGroup: React.FC<PanelWarningCalloutGroupProps> = ({
  warnings,
  onRetry,
}) => {
  if (!warnings.length) return null

  return (
    <>
      {warnings.map((w) => {
        // Stable key on warning code (per-panel codes are unique — a panel has
        // at most one MULTI_FETCH_PARTIAL_FAILURE and one PRIOR_SEASON_NO_DATA).
        // More stable than array index if BE reorders the warning stack.
        if (w.code === 'PRIOR_SEASON_NO_DATA') {
          return <NoPriorDataCallout key={w.code} warning={w} />
        }
        if (w.code === 'MULTI_FETCH_PARTIAL_FAILURE') {
          return <PartialFailureCallout key={w.code} warning={w} onRetry={onRetry} />
        }
        // Unknown code — silently skip (forward compat)
        return null
      })}
    </>
  )
}
